/**
 * Sentry 初始化和错误捕获模块
 */
import * as Sentry from '@sentry/vue';
import { captureConsoleIntegration } from '@sentry/integrations';
import {
  SENTRY_DSN,
  isSentryEnabled,
  getSampleRates,
  beforeSend,
  beforeSendTransaction,
  getEnvironment,
  getRelease,
  getTags,
} from './sentry.config';
import { getCurrentUserInfo } from './user.helper';

let isInitialized = false;

/**
 * 初始化 Sentry
 * @param {Object} app - Vue 应用实例
 */
export const initSentry = (app) => {
  if (!isSentryEnabled() || isInitialized) {
    console.log('[Sentry] Sentry is disabled or already initialized');
    return;
  }

  try {
    const sampleRates = getSampleRates();
    
    Sentry.init({
      app,
      dsn: SENTRY_DSN,
      environment: getEnvironment(),
      release: getRelease(),
      
      // 采样率配置
      tracesSampleRate: sampleRates.tracesSampleRate,
      
      // 错误过滤和数据清理
      beforeSend,
      beforeSendTransaction,
      
      // 集成配置
      integrations: [
        // 捕获控制台错误（使用新的函数式集成）
        captureConsoleIntegration({
          levels: ['error'],
        }),
      ],
      
      // 默认标签
      initialScope: {
        tags: getTags(),
      },
      
      // 性能配置
      maxBreadcrumbs: 50,  // 最多保留 50 条面包屑
      attachStacktrace: true,  // 附加堆栈跟踪
      
      // 错误分组
      // fingerprint 可以在具体捕获错误时设置
    });
    
    isInitialized = true;
    console.log('[Sentry] Initialized successfully');
  } catch (error) {
    console.error('[Sentry] Initialization failed:', error);
  }
};

/**
 * 设置用户信息
 * @param {Object} userInfo - 用户信息（可选，如果不传则从 localStorage 读取 phone）
 */
export const setSentryUser = (userInfo) => {
  if (!isInitialized) return;
  
  try {
    let user = null;
    
    if (userInfo) {
      // 如果传入了用户信息
      user = {
        id: userInfo.id || userInfo.userId || userInfo.phone,
        username: userInfo.username || userInfo.userName || userInfo.phone,
        phone: userInfo.phone,
      };
    } else {
      // 如果没有传入，尝试从 localStorage 读取 phone
      const currentUser = getCurrentUserInfo();
      if (currentUser) {
        user = currentUser;
      }
    }
    
    if (user) {
      Sentry.setUser(user);
      console.log('[Sentry] User set:', user.phone || user.id);
    }
  } catch (error) {
    console.error('[Sentry] Failed to set user:', error);
  }
};

/**
 * 清除用户信息（退出登录时调用）
 */
export const clearSentryUser = () => {
  if (!isInitialized) return;
  
  try {
    Sentry.setUser(null);
    console.log('[Sentry] User cleared');
  } catch (error) {
    console.error('[Sentry] Failed to clear user:', error);
  }
};

/**
 * 手动捕获异常
 * @param {Error} error - 错误对象
 * @param {Object} context - 上下文信息
 */
export const captureError = (error, context = {}) => {
  if (!isInitialized) {
    console.error('[Sentry] Not initialized, error:', error);
    return;
  }
  
  try {
    // 确保用户信息已设置（从 localStorage 自动获取）
    const currentUser = getCurrentUserInfo();
    if (currentUser) {
      Sentry.setUser(currentUser);
    }
    
    Sentry.captureException(error, {
      contexts: context,
    });
  } catch (e) {
    console.error('[Sentry] Failed to capture error:', e);
  }
};

/**
 * 手动发送消息
 * @param {String} message - 消息内容
 * @param {String} level - 级别 (debug/info/warning/error/fatal)
 */
export const captureMessage = (message, level = 'info') => {
  if (!isInitialized) return;
  
  try {
    Sentry.captureMessage(message, level);
  } catch (error) {
    console.error('[Sentry] Failed to capture message:', error);
  }
};

/**
 * 添加面包屑（用户行为追踪）
 * @param {Object} breadcrumb - 面包屑数据
 */
export const addBreadcrumb = (breadcrumb) => {
  if (!isInitialized) return;
  
  try {
    Sentry.addBreadcrumb(breadcrumb);
  } catch (error) {
    console.error('[Sentry] Failed to add breadcrumb:', error);
  }
};

/**
 * 设置标签
 * @param {String} key - 标签键
 * @param {String} value - 标签值
 */
export const setTag = (key, value) => {
  if (!isInitialized) return;
  
  try {
    Sentry.setTag(key, value);
  } catch (error) {
    console.error('[Sentry] Failed to set tag:', error);
  }
};

/**
 * 设置上下文
 * @param {String} name - 上下文名称
 * @param {Object} context - 上下文数据
 */
export const setContext = (name, context) => {
  if (!isInitialized) return;
  
  try {
    Sentry.setContext(name, context);
  } catch (error) {
    console.error('[Sentry] Failed to set context:', error);
  }
};

export default {
  initSentry,
  setSentryUser,
  clearSentryUser,
  captureError,
  captureMessage,
  addBreadcrumb,
  setTag,
  setContext,
};
